import { useQuery } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import { CreditCard, Zap, FileText, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useActiveSubscription } from "@/hooks/useSubscription";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { payments } from "@/lib/payments/adapter";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { toast } from "sonner";
import { format } from "date-fns";

export default function AccountBilling() {
  const { user, loading } = useAuth();
  const { data: sub, refetch } = useActiveSubscription();

  const { data: boosts } = useQuery({
    queryKey: ["my-boosts", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("idea_boosts")
        .select("*, ideas(title), boost_packages(name, duration_days)")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  const { data: invoices } = useQuery({
    queryKey: ["my-invoices", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("billing_invoices")
        .select("*")
        .eq("user_id", user!.id)
        .order("issued_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  if (loading) return null;
  if (!user) return <Navigate to="/auth?redirect=/account/billing" replace />;

  const cancel = async () => {
    if (!sub) return;
    try {
      await payments.cancelSubscription(sub.id);
      toast.success("Will cancel at period end");
      refetch();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PaymentTestModeBanner />
      <div className="container max-w-4xl mx-auto px-4 py-10">
        <h1 className="font-display text-3xl font-bold text-foreground mb-6">Billing</h1>

        <section className="rounded-xl border border-border bg-card p-6 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="h-4 w-4 text-primary" />
            <h2 className="font-display font-semibold text-foreground">Current plan</h2>
          </div>
          {sub ? (
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="text-xl font-bold text-foreground">{sub.billing_plans?.name}</div>
                <div className="text-sm text-muted-foreground">
                  ${(sub.billing_plans?.price_cents / 100).toFixed(0)}/{sub.billing_plans?.interval}
                  {" · "}
                  Renews {format(new Date(sub.current_period_end), "MMM d, yyyy")}
                </div>
                {sub.cancel_at_period_end && (
                  <Badge variant="outline" className="mt-2">Cancels at period end</Badge>
                )}
              </div>
              <div className="flex gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to="/pricing">Change plan</Link>
                </Button>
                {!sub.cancel_at_period_end && (
                  <Button onClick={cancel} variant="ghost" size="sm">
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-muted-foreground">No active subscription — you're on the free plan.</div>
              <Button asChild>
                <Link to="/pricing">View plans <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-border bg-card p-6 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-amber-400" />
            <h2 className="font-display font-semibold text-foreground">Active & past boosts</h2>
          </div>
          {boosts?.length ? (
            <div className="space-y-2">
              {boosts.map((b) => {
                const active = new Date(b.ends_at) > new Date() && b.status === "active";
                return (
                  <div key={b.id} className="flex items-center justify-between text-sm border-b border-border last:border-0 py-2">
                    <div>
                      <div className="text-foreground font-medium">{b.ideas?.title || "Idea"}</div>
                      <div className="text-xs text-muted-foreground">
                        {b.boost_packages?.name} · ends {format(new Date(b.ends_at), "MMM d")}
                      </div>
                    </div>
                    <Badge variant={active ? "default" : "outline"}>{active ? "Active" : "Ended"}</Badge>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No boosts yet.</p>
          )}
        </section>

        <section className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-display font-semibold text-foreground">Invoices</h2>
          </div>
          {invoices?.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground uppercase">
                  <th className="py-2">Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-border">
                    <td className="py-2 text-foreground">{format(new Date(inv.issued_at), "MMM d, yyyy")}</td>
                    <td className="capitalize text-foreground">{inv.kind}</td>
                    <td className="text-foreground">${(inv.amount_cents / 100).toFixed(2)}</td>
                    <td><Badge variant="outline" className="capitalize">{inv.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-muted-foreground">No invoices yet.</p>
          )}
        </section>

        <p className="text-xs text-muted-foreground mt-6 text-center">
          Need help with a charge? Contact support — we'll sort it out.
        </p>
      </div>
    </div>
  );
}